import { ReactNode } from 'react';
import { ProTooltip, ProImage } from '../../antd';
import Images from '../../Images';
import usePrevious from '../../Hooks/usePrevious';
import { useLayoutContext } from '../../PageLayout/context/LayoutContext';
import { renderEnumNames } from '../../utils/renderUtils';
import { FormInstance } from 'antd/lib/form';
import { EdiTableVariableProps, EditColumnType } from '../interface';
import { TABLE_COLUMN_EMPTY } from '../../constants/constant';

function ediTableColumnsHook(props: EdiTableVariableProps, form: FormInstance) {
  const { columns, disabled } = props;
  const { enumData } = useLayoutContext();
  const prevDisabled = usePrevious(disabled);
  const editColumnNames: string[] = [];
  const formatColumns = (): EditColumnType[] => {
    let newColumns: EditColumnType[] = [];
    if (Array.isArray(columns) && columns.length) {
      const baseColumnsProps: EditColumnType = {
        ellipsis: {
          showTitle: false,
        },
      };
      // @ts-ignore
      newColumns = columns?.map((column: EditColumnType) => {
        const {
          editable,
          title,
          dataIndex,
          enumCode,
          renderType,
          formItemType,
          optionsKey,
          defaultPropsKey,
          enumCodeKey,
          requiredKey,
          required,
          ...restProps
        } = column;
        if (editable) {
          if (typeof dataIndex === 'string') editColumnNames.push(dataIndex);
          return {
            ...baseColumnsProps,
            ...column,
            shouldCellUpdate: (record, prevRecord) => {
              if (disabled !== prevDisabled) return true;
              if (typeof dataIndex === 'string') {
                return record[dataIndex] !== prevRecord[dataIndex];
              }
              return false;
            },
            onCell: (record: any, recordIndex?: number) => {
              let initialValue: any;
              const columnFormType = formItemType ? record[formItemType] : '';
              if (typeof dataIndex === 'string')
                initialValue = record[dataIndex];
              if (
                columnFormType === 'SelectTable' &&
                Array.isArray(dataIndex)
              ) {
                initialValue = record[dataIndex[0]];
              }
              return {
                record,
                editable,
                disabled,
                form,
                defaultPropsKey,
                enumCodeKey,
                rowIndex: recordIndex,
                optionsKey,
                formItemProps: {
                  required: requiredKey ? record[requiredKey] : required,
                  enumCode,
                  formItemType: columnFormType,
                  label: title,
                  name: `${dataIndex}_${recordIndex}`,
                  initialValue,
                  ...restProps,
                },
              };
            },
          };
        }
        if (enumCode && enumData) {
          const enumItem = enumData[`${enumCode}`];
          return enumItem
            ? {
                ...baseColumnsProps,
                ...column,
                render: (val: any) => {
                  return renderEnumNames(val, enumItem.items);
                },
              }
            : { ...baseColumnsProps, ...column };
        }
        if (Array.isArray(dataIndex)) {
          return {
            ...baseColumnsProps,
            ...column,
            render: (val: any, rowData: any) => {
              return (
                <>
                  {dataIndex.map(item => (
                    <ProTooltip
                      key={item}
                      placement="topLeft"
                      title={rowData[item]}
                      mouseEnterDelay={1}
                    >
                      <div className="ant-table-cell-ellipsis">
                        {rowData[item] || TABLE_COLUMN_EMPTY}
                      </div>
                    </ProTooltip>
                  ))}
                </>
              );
            },
          };
        }
        switch (renderType) {
          case 'image':
            return {
              ...baseColumnsProps,
              className: 'table-image-column',
              ...column,
              render: (val: string) => {
                return <ProImage className="table-column-image" src={val} />;
              },
            };
          case 'images':
            return {
              ...column,
              width: column.width || 280,
              render: (vals: any) => {
                if (Array.isArray(vals)) return <Images images={vals} />;
                return null;
              },
            };
          case 'boolean':
            return {
              ...baseColumnsProps,
              ...column,
              render: (vals: any) => {
                return vals ? '是' : '否';
              },
            };
        }
        return {
          ...baseColumnsProps,
          ...column,
          render: (val: any, record: any, key: number) => {
            if (val === undefined || val === '' || val === null) {
              return column.render
                ? column.render(val, record, key)
                : TABLE_COLUMN_EMPTY;
            }
            if (typeof val === 'string') {
              let currentRender = column.render
                ? column.render(val, record, key)
                : val;
              if (record.required) {
                currentRender = (
                  <div className="edit-table-column-required">
                    {currentRender as ReactNode}
                  </div>
                );
              }
              return (
                <ProTooltip placement="topLeft" title={val}>
                  {currentRender as ReactNode}
                </ProTooltip>
              );
            }
            return column.render ? column.render(val, record, key) : val;
          },
        };
      });
    }
    return newColumns;
  };
  return {
    editColumnNames,
    columns: formatColumns(),
  };
}

export default ediTableColumnsHook;
